import { animate, style, transition, trigger } from '@angular/animations';
import { NgForOf, NgIf } from '@angular/common';
import { Component, HostBinding, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';

export interface ChildrenMenuItem {
	title: string;
	icon: string;
	path: string;
	/** Non-clickable item (e.g. "Coming soon"). */
	disabled?: boolean;
	/** Native tooltip shown on hover (used for disabled items). */
	tooltip?: string;
	/**
	 * Routes that count as being ON this item, as path-segment prefixes. Absent
	 * means the item owns its own `path` and everything beneath it; say more
	 * when a guard can land the reader somewhere else — see SlSubmenuItem.
	 */
	matches?: string[];
}

@Component({
	standalone: true,
	selector: 'app-children-menu',
	template: `
		<div class="wrapper">
			<ng-container *ngFor="let item of menuItems">
				<a
					*ngIf="!item.disabled"
					class="menu-item"
					[routerLink]="item.path"
					(click)="clickItem.next()"
				>
					<app-icon type="svg" [size]="16" color="var(--color)">
						{{ item.icon }}
					</app-icon>
					<typography variant="p/sm_medium">
						{{ item.title }}
					</typography>
				</a>

				<div
					*ngIf="item.disabled"
					class="menu-item disabled"
					[title]="item.tooltip || ''"
				>
					<app-icon type="svg" [size]="16" color="var(--color)">
						{{ item.icon }}
					</app-icon>
					<typography variant="p/sm_medium">
						{{ item.title }}
					</typography>
				</div>
			</ng-container>
		</div>
	`,
	imports: [IconComponent, TypographyComponent, NgForOf, NgIf, RouterLink],
	animations: [
		trigger('fade', [
			transition(':enter', [
				style({ opacity: 0 }),
				animate('150ms ease-out', style({ opacity: 1 }))
			]),
			transition(':leave', [
				animate('150ms ease-in', style({ opacity: 0 }))
			])
		])
	],
	styles: [
		`
			:host {
				--color: #7a7a7a;
				display: block;

				.wrapper {
					display: flex;
					align-items: center;
					width: 13rem;
					padding: 0.5rem;
					flex-direction: column;
					row-gap: 0.25rem;
					border-radius: 1rem;
					border: 1px solid #d6e7ff;
					background: #fff;
					box-shadow: 0 0 11.8px 0 rgba(35, 78, 143, 0.15);

					.menu-item {
						display: flex;
						align-items: center;
						column-gap: 0.5rem;
						height: 2.25rem;
						padding: 0 0.75rem;
						background-color: #fff;
						border-radius: 1rem;
						transition: background-color 150ms;
						flex-shrink: 0;
						width: 100%;

						&:hover {
							--color: #2872e1;
							background-color: #e6effc;
						}

						&.disabled {
							cursor: not-allowed;
							opacity: 0.5;

							&:hover {
								--color: #7a7a7a;
								background-color: #fff;
							}
						}

						typography {
							transition: color 150ms;
							color: var(--color);
						}
					}
				}
			}
		`
	]
})
export class ChildrenMenuDialog {
	@HostBinding('@fade') fade = true;

	@Input() menuItems: ChildrenMenuItem[] = [];

	clickItem = new Subject<void>();
}
